import * as React from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Modal,
  FormControlLabel,
  RadioGroup,
  Radio,
  SelectChangeEvent,
} from "@mui/material";
import BusinessForm from "./businessForm";
import { UserContext } from "../../auth/auth-context";
import { StyledGrid } from "../../../assets/css/styledComponent";
import { useStyles } from "./styles";
import { useMutation, useQuery } from "@apollo/client";
import SubscriptionButton, {
  PaypalPlanId,
} from "../../payment plans/subscriptionButton";
import { GET_PLANS } from "../../../graphql/queries";
import WorkingHourView from "../workingHourView";
import { EntityType } from "../../../redux/actions/businessActions";
import ImageEntryForm from "../image/imageEntryForm";
import PaymentSettings from "../../../pages/customer/booking/paymentSettings";
import { useIsMobile } from "../../../util/utils";
import { planEnv } from "../../../util/link";
import {
  GET_SUBSCRIPTION,
  UPDATE_SUBSCRIPTION,
} from "../../../graphql/mutations/subscription";
import { useSelectedBusiness } from "../../../redux/store";
import { Subscription } from "../../../types";
import { SEND_EMAIL_MUTATION } from "../../../graphql/mutations";
import { useParams } from "react-router-dom";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface BusinessDetailHorizontalTabProps {
  isDisabled: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, padding: "1px" }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const getSubscriptionStatusMessage = (
  isUpgrade: boolean,
  isDowngrade: boolean,
  nextBillingDate: string,
  plans: any,
  pendingPlanId?: number | null,
  currentPlanId?: number | null
) => {
  const commonSx = {
    textAlign: "left",
    letterSpacing: "normal",
    wordSpacing: "normal",
    display: "block",
  };

  // Helper: Determine if the new plan is Free.
  const isFree = pendingPlanId === 1;
  const currentPlanName = plans?.find(
    (plan: any) => Number(plan.id) === currentPlanId
  )?.name;

  const pendingPlanName = plans?.find(
    (plan: any) => Number(plan.id) === pendingPlanId
  )?.name;

  if (isUpgrade) {
    return (
      <Typography variant="caption" color="textSecondary" sx={commonSx}>
        Your upgrade is scheduled to take effect at the start of your next
        billing cycle ({nextBillingDate}).
        {!isFree && " Your new payment will begin at that time."}
      </Typography>
    );
  } else if (isDowngrade) {
    return (
      <Typography variant="caption" color="textSecondary" sx={commonSx}>
        Your current {currentPlanName} plan will remain active until{" "}
        {nextBillingDate}.{" "}
        {pendingPlanName && !isFree ? (
          <>
            A downgrade to {pendingPlanName} is scheduled, and your new payment
            will start with the next cycle.
          </>
        ) : (
          <>
            A downgrade to {pendingPlanName} is scheduled and will take effect
            after your billing cycle.
          </>
        )}
      </Typography>
    );
  }
  return null;
};

export const BusinessDetailHorizontalTab: React.FC<
  BusinessDetailHorizontalTabProps
> = ({ isDisabled }) => {
  const { tabId } = useParams();
  const [value, setValue] = React.useState(tabId != null ? Number(tabId) : 0); 
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const userContext = React.useContext(UserContext);
  const token = userContext.providerUser;
  const selectedBusiness = useSelectedBusiness();
  const disableOtherTabs = token.defaultBusinessId == null;
  // token.planId holds the current subscription plan (1: Free, 2: Standard, 3: Team)
  const [selectedPlanId, setSelectedPlanId] = React.useState(token.planId);
  const [billingPeriod, setBillingPeriod] = React.useState("Monthly");
  const [sendSubscriptionEmail] = useMutation(SEND_EMAIL_MUTATION);

  const { data } = useQuery(GET_PLANS, {
    variables: { planEnv: planEnv }, // Pass envPlan as a variable
  });
  const [subscription, setSubscription] = React.useState<Subscription | null>(
    null
  );
  const {
    loading: loadingSubscription,
    error: errorSubscription,
    data: dataSubscription,
  } = useQuery(GET_SUBSCRIPTION, {
    variables: { businessUrl: selectedBusiness?.businessBasic?.url },
    skip: !selectedBusiness?.businessBasic?.url,
  });

  React.useEffect(() => {
    if (dataSubscription && dataSubscription.getSubscription) {
      setSubscription({
        ...dataSubscription.getSubscription,
        businessBasic: dataSubscription.getSubscription,
      });
    }
  }, [dataSubscription, selectedPlanId]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    // If other tabs are disabled, always force value 0 (Business Basic)
    if (disableOtherTabs && newValue !== 0) {
      setValue(0);
    } else {
      setValue(newValue);
    }
  };

  const handleSelectChange = (
    event: SelectChangeEvent<number>,
    child: React.ReactNode
  ) => {
    const newValue = Number(event.target.value);
    if (disableOtherTabs && newValue !== 0) {
      setValue(0);
    } else {
      setValue(newValue);
    }
  };
  const menuItems = [
    { label: "Business Basic", index: 0 },
    { label: "Business Hour", index: 1 },
    { label: "Images", index: 2 },
    { label: "Payment Integration", index: 3 },
    { label: "Subscription & Billing", index: 4 },
  ];

  // Update the handler to use SelectChangeEvent<number>
  const handlePlanChange = (event: SelectChangeEvent<number>) => {
    setSelectedPlanId(Number(event.target.value));
  };

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION);

  if (loadingSubscription) {
    return <div>Loading...</div>;
  }
  if (errorSubscription) {
    return <div>Error...</div>;
  }

  const handleSubscriptionUpdate = async (
    selectedPlanId: any,
    duration: any,
    plans: any
  ) => {
    const plan = data?.plans?.find(
      (plan: any) => Number(plan.id) === selectedPlanId
    );
    const currentPlanId = Number(token.planId);
    const currentPlan = data?.plans?.find(
      (plan: any) => Number(plan.id) === currentPlanId
    );
    // Your subscription update logic here (for downgrades or unsubscribe)
    try {
      const newTokenResponse = await updateSubscription({
        variables: {
          input: {
            userId: token.userId,
            subscriptionId: Number(token.subscriptionId),
            selectedPlanId: Number(selectedPlanId),
            staffIdSubscibeForBusiness: Number(token.staffId),
            // Set pending fields for the downgrade.
            pendingPaypalPlanId: PaypalPlanId(plan, duration),
          },
        },
      });
      localStorage.setItem("token", newTokenResponse.data.updateSubscription);
      // Prepare email notification.
      const emailSubject =
        "Your Subscription Update: Transitioning to Free Plan";
      const emailBody = `Hello,

      We're sorry to see you moving away from our advanced features.
      Your current ${currentPlan.name} plan will remain active until the end of your billing cycle, and then you'll be transitioned to the Free plan.
      We truly appreciate your support and hope you'll consider returning to our premium offerings in the future.
      
      Thank you,
      Your Team`;

      try {
        await sendSubscriptionEmail({
          variables: {
            to: token.email,
            subject: emailSubject,
            text: emailBody,
          },
        });
      } catch (emailError) {
        console.error("Error sending subscription email:", emailError);
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      alert("An error occurred while cancelling the subscription.");
    }
    handleCloseModal();
  };

  interface SubscriptionContentProps {
    isDisabled?: boolean;
  }

  const SubscriptionContent: React.FC<SubscriptionContentProps> = () => {
    // Determine current plan from token and any pending plan from subscription (if available)
    const currentPlanId = Number(token.planId) ?? 1;
    // Use subscription.pendingPlanId if it exists, otherwise use the locally selected planId
    const pendingPlanId =
      currentPlanId > 1 && subscription?.pendingPlanId
        ? Number(subscription.pendingPlanId)
        : null;

    const isUpgrade = selectedPlanId > currentPlanId;
    // For downgrade, consider if the displayed (pending) plan is lower than the current plan
    const isDowngrade =
      pendingPlanId !== null
        ? pendingPlanId < currentPlanId
        : Number(selectedPlanId) < currentPlanId;

    // Get next billing date from subscription (if available) for pending downgrade
    const nextBillingDate = subscription?.pendingEffectiveDate
      ? new Date(subscription.pendingEffectiveDate).toLocaleDateString()
      : "the end of your billing cycle";

    return (
      <Typography
        variant="body1"
        align="justify"
        sx={{ pl: 2, pr: 2, pt: 2, pb: 2 }}
      >
        <Typography paragraph>
          Here you can manage your subscription and billing details.
        </Typography>
        {getSubscriptionStatusMessage(
          isUpgrade,
          isDowngrade,
          nextBillingDate,
          data?.plans,
          pendingPlanId,
          currentPlanId
        )}
        <FormControl margin="normal" fullWidth>
          <InputLabel id="plan-label">Plan</InputLabel>
          <Select
            label="Select a Plan"
            // Use local state planId so user can select a new plan; if a pending downgrade exists, you might want to show it separately.
            value={selectedPlanId}
            onChange={handlePlanChange}
            style={{ minWidth: "150px" }}
          >
            {data?.plans?.map((plan: any) => (
              <MenuItem
                key={plan.id}
                value={plan.id}
                // Disable selection if it's the current or pending plan.
                disabled={
                  isDisabled
                    ? true
                    : Number(plan.id) === currentPlanId ||
                      Number(plan.id) === pendingPlanId
                }
              >
                {plan.name}{" "}
                {Number(plan.id) === currentPlanId &&
                  `(current ${token?.subscriptionPlanType} plan)`}
                {Number(plan.id) === pendingPlanId && "(pending)"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl margin="normal" fullWidth>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenModal}
            // Disable if the user hasn't changed their selection
            disabled={Number(selectedPlanId) === currentPlanId}
          >
            Update Subscription
          </Button>
        </FormControl>
        <Modal
          open={isModalOpen}
          onClose={handleCloseModal}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography id="modal-title" variant="h6" component="h2">
              Update Subscription
            </Typography>
            <Typography id="modal-description" sx={{ mt: 2 }}>
              Are you sure you want to update your subscription?
            </Typography>
            {selectedPlanId === 1 ? (
              // Free plan: treat as unsubscribe
              <div>
                <Typography>
                  You are about to unsubscribe from the{" "}
                  {
                    data?.plans?.find(
                      (plan: any) => Number(plan.id) === currentPlanId
                    )?.name
                  }{" "}
                  plan. We are sorry to see you go.
                </Typography>
                <Box
                  sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                >
                  <Button onClick={handleCloseModal} sx={{ mr: 2 }}>
                    Cancel
                  </Button>
                  {selectedPlanId === 1 && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        handleSubscriptionUpdate(
                          selectedPlanId,
                          billingPeriod,
                          data?.plans
                        )
                      }
                    >
                      Confirm Update
                    </Button>
                  )}
                </Box>
              </div>
            ) : (
              <FormControl>
                <Typography>
                  Update to plan:{" "}
                  {
                    data?.plans?.find(
                      (plan: any) => Number(plan.id) === selectedPlanId
                    )?.name
                  }
                </Typography>
                {getSubscriptionStatusMessage(
                  isUpgrade,
                  isDowngrade,
                  nextBillingDate,
                  data?.plans,
                  selectedPlanId
                )}
                <RadioGroup
                  row
                  aria-label="billing period"
                  name="billing period"
                  value={billingPeriod}
                  onChange={(e) => setBillingPeriod(e.target.value)}
                >
                  <FormControlLabel
                    value="Monthly"
                    control={<Radio />}
                    label="Monthly"
                  />
                  <FormControlLabel
                    value="Yearly"
                    control={<Radio />}
                    label="Yearly"
                  />
                </RadioGroup>
                <Typography>
                  Price:{" "}
                  {billingPeriod === "Monthly"
                    ? data?.plans?.find(
                        (plan: any) => Number(plan.id) === selectedPlanId
                      )?.priceMonthly
                    : data?.plans?.find(
                        (plan: any) => Number(plan.id) === selectedPlanId
                      )?.priceYearly}{" "}
                  $
                </Typography>
                <SubscriptionButton
                  plans={data?.plans}
                  selectedPlanId={selectedPlanId}
                  duration={billingPeriod}
                  token={token}
                />
              </FormControl>
            )}
          </Box>
        </Modal>
      </Typography>
    );
  };

  return (
    <StyledGrid container spacing={2} sx={{ marginTop: 3 }}>
      {/* Left column for tabs */}
      <StyledGrid item xs={12} md={3}>
        {isMobile ? (
          // Dropdown for mobile view
          <FormControl fullWidth>
            <InputLabel id="tab-select-label">Select</InputLabel>
            <Select
              labelId="tab-select-label"
              id="tab-select"
              value={value}
              onChange={handleSelectChange}
              label="Select"
            >
              {menuItems.map((item) => (
                <MenuItem
                  key={item.index}
                  value={item.index}
                  disabled={disableOtherTabs && item.index !== 0}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          // Tabs for desktop view
          <Box
            sx={{
              overflowY: "auto",
              maxHeight: "calc(100vh - 100px)",
              borderRight: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Tabs
              orientation="vertical"
              value={value}
              onChange={handleChange}
              aria-label="business tabs"
              variant="standard"
              scrollButtons="auto"
              sx={{
                "& .MuiTab-root": {
                  fontWeight: "bold",
                  fontSize: "16px",
                },
                "& .Mui-selected": {
                  color: theme.palette.primary.main,
                },
              }}
            >
              {menuItems.map((item) => (
                <Tab
                  key={item.index}
                  label={item.label}
                  {...a11yProps(item.index)}
                  disabled={disableOtherTabs && item.index !== 0}
                />
              ))}
            </Tabs>
          </Box>
        )}
      </StyledGrid>

      {/* Right column for content */}
      <Grid item xs={12} md={9} className={classes.customGridContainer}>
        <TabPanel value={value} index={0}>
          <BusinessForm isDisabled={isDisabled} />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <WorkingHourView
            key={Math.floor(Math.random() * 1000)}
            providerUser={token}
            isDisabled={isDisabled}
            entityType={EntityType.BUSINESS}
            isCustom={false}
            defaultWH={true}
          />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <ImageEntryForm singleImageMode={false} isDisabled={isDisabled} />
        </TabPanel>
        <TabPanel value={value} index={3}>
          <PaymentSettings isDisabled={isDisabled} />
        </TabPanel>
        <TabPanel value={value} index={4}>
          <SubscriptionContent />
        </TabPanel>
      </Grid>
    </StyledGrid>
  );
};

export default BusinessDetailHorizontalTab;
